import EcryptService from "./EncryptService";
const Cookies  = require("vue-cookies");

// #region Constants

/**
* @summary Nome do cookie de autenticação responsavel por armazenar o JWT token.
*/
const AUTH_COOKIE_NAME = "APIS";

/**
* @summary Nome do cookie que verifica se é para manter o login como permanente ou não.
*/
const COOKIE_SESSION_TIME_NAME = "STIME";

/**
 * @summary Nome do cookie que armazena o tempo de expiração de um token JWT.
*/
const JWT_SESSION_TIME_NAME = "JST";

/**
 * @summary Flag para informar se o sistema será https ou não quando criar o cookie.
*/
const isHttps = process.env.VUE_APP_IS_HTTPS === "true";

// #endregion

// #region Methods

/**
 * @summary Criptografa qualquer string..
 * @param {string} text - Qualquer texto.
 * @return {string} String Criptografada.
 */
const CipherText = EcryptService.Cipher(process.env.VUE_APP_TOKEN_SECRET);

/**
 * @summary Retorna descriptografada a string.
 * @param {string} text - Uma string cryptografada
 * @return {string} string descriptografada.
 */
const DecipherText = EcryptService.Decipher(process.env.VUE_APP_TOKEN_SECRET);

/**
 * @summary Busca o valor do JWT guardado no Cookie da sessão.
 * @returns {string} JWT.
 */
const GetSessionCookie = function()
{
	return Cookies.get(AUTH_COOKIE_NAME);
};

/**
 * @summary Busca o valor do tempo da sessão.
 * Guardado no Cookie responsável por identificar se a sessão é permanente ou não
 * @returns {string} 1d para sessão sem login permanente ou Infinity para login permanente.
 */
const GetSessionExpiredAtCookie = function()
{
	return DecipherText(Cookies.get(COOKIE_SESSION_TIME_NAME));
};

/**
 * @summary Busca o valor do tempo da sessão.
 * Guardado no Cookie responsável por identificar o tempo de expiração do JWT.
 * @returns {string} data e hora da expiração do JWT.
 */
const GetJWTExpiredAtCookie = function()
{
	return DecipherText(Cookies.get(JWT_SESSION_TIME_NAME));
};

/**
 * @summary Cria o Cookie de Sessão.
 * @param {object} accessToken - resposta do back-end quando usuário é logado.
 * @param {boolean} isPermanentLogin - Verifica se o usuário gostaria de permanecer logado.
 */
const CreateSessionCookie = function(accessToken, isPermanentLogin = false)
{
	const expireCookieDate = isPermanentLogin ? Infinity : "1d";
	const settings = GetSessionCookieConfigurations(accessToken.token, expireCookieDate, isHttps);

	Cookies.set(settings.name,
		 settings.value,
		 settings.expires,
		 settings.path,
		 settings.domain,
		 settings.secure,
		 settings.sameSite);

	CreateSessionCookieExpiredAt(expireCookieDate);
	CreateJWTExpiredAtCookie(accessToken.expiration);
};

/**
 * @summary Cria um novo Cookie de sessão.
 * @param {object} accessToken - resposta do back-end quando usuário pede um novo token de acesso.
 */
const RefreshSessionCookie = function(accessToken)
{
	const expiredSessionAt = GetSessionExpiredAtCookie();

	DeleteSessionCookie();
	DeleteSessionCookieJWTExpiredAt();

	const expireCookieDate =  expiredSessionAt === "1d" ? "1d" : Infinity;
	DeleteSessionExpiredAtCookie();

	const settings = GetSessionCookieConfigurations(accessToken.token, expireCookieDate, isHttps);

	Cookies.set(settings.name,
		 settings.value,
		 settings.expires,
		 settings.path,
		 settings.domain,
		 settings.secure,
		 settings.sameSite);

	CreateSessionCookieExpiredAt(expireCookieDate);
	CreateJWTExpiredAtCookie(accessToken.expiration);
};

/**
 * @summary Cria um novo Cookie que guarda o tempo de sessão.
 * @param {string} expires Tempo para expirar o cookie da sessão.
 */
const CreateSessionCookieExpiredAt = function(expires)
{
	const cypheredValue = CipherText(expires);
	const settings = GetSessionCookieExpiredAtConfigurations(cypheredValue, isHttps);

	Cookies.set(settings.name,
		settings.value,
		settings.expires,
		settings.path,
		settings.domain,
		settings.secure,
		settings.sameSite);
};

/**
 * @summary Cria um novo Cookie que guarda o tempo de validade do JWT.
 * @param {string} expires Tempo para expirar o JWT da sessão.
 */
const CreateJWTExpiredAtCookie = function(expires)
{
	const cypheredValue = CipherText(expires);
	const settings = GetSessionCookieJWTExpiredAtConfigurations(cypheredValue, isHttps);
	Cookies.set(settings.name,
		settings.value,
		settings.expires,
		settings.path,
		settings.domain,
		settings.secure,
		settings.sameSite);
};

/**
 * @summary Remove o Cookie responsável pela sessão e por guardar o JWT.
 */
const DeleteSessionCookie = function()
{
	Cookies.remove(AUTH_COOKIE_NAME);
};

/**
 * @summary Remove o Cookie responsável por identificar se a sessão é permanente ou não.
 */
const DeleteSessionExpiredAtCookie = function()
{
	Cookies.remove(COOKIE_SESSION_TIME_NAME);
};

/**
 * @summary Remove o Cookie responsável por guardar o tempo de expiração do JWT.
 */
const DeleteSessionCookieJWTExpiredAt = function()
{
	Cookies.remove(JWT_SESSION_TIME_NAME);
};

/**
 * @summary Configurações do cookie da sessão.
 * @param {string} value -  valor a ser guardado no cookie.
 * @param {string} expires - "1d" ou Infinity
 * @returns {object} Configuração do Cookie.
 */
const GetSessionCookieConfigurations = function(value, expires, isHttps)
{
	return {
		name:     AUTH_COOKIE_NAME,
		value:    value,
		expires:  expires,
		path:     null,
		domain:   null,
		secure:   isHttps,
		sameSite: "Strict"
	 };
};

/**
 * @summary Configurações do cookie que guarda o tempo da sessão.
 * @param {string} value -  valor a ser guardado no cookie. 1d ou Infinity.
 * @returns {object} Configuração do Cookie.
 */
const GetSessionCookieExpiredAtConfigurations = function(value, isHttps)
{
	return {
		name:     COOKIE_SESSION_TIME_NAME,
		value:    value,
		expires:  Infinity,
		path:     null,
		domain:   null,
		secure:   isHttps,
		sameSite: "Strict"
	 };
};

/**
 * @summary Configurações do cookie que guarda o tempo do JWT.
 * @param {string} value -  valor a ser guardado no cookie. Data com dia, mes, ano, horas e minutos.
 * @returns {object} Configuração do Cookie.
 */
const GetSessionCookieJWTExpiredAtConfigurations = function(value, isHttps)
{
	return {
		name:     JWT_SESSION_TIME_NAME,
		value:    value,
		expires:  Infinity,
		path:     null,
		domain:   null,
		secure:   isHttps,
		sameSite: "Strict"
	 };
};

// #endregion

export default
{
	CreateSessionCookie,
	DecipherText,
	DeleteSessionCookie,
	DeleteSessionCookieJWTExpiredAt,
	DeleteSessionExpiredAtCookie,
	GetSessionCookie,
	GetSessionExpiredAtCookie,
	GetJWTExpiredAtCookie,
	RefreshSessionCookie,
};
